<template>
  <section class="ic-home-hero ic-lhero-wrap">
    <div class="ic-lhero">
      <div class="ic-lhero__content">
        <p v-if="totalCount > 0" class="ic-lhero__eyebrow" data-testid="hero-count">
          <span class="ic-lhero__dot" aria-hidden="true"></span>
          {{ totalCountLabel }}
        </p>

        <h1 class="ic-lhero__title" :aria-label="titleText" translate="no">
          <template v-for="(word, i) in titleWords" :key="i"><span
            class="ic-hero-word"
            :style="{ '--d': word.delay + 's' }"
            aria-hidden="true"
          >{{ word.text }}</span><span
            v-if="word.breakAfter"
            class="ic-hero-break"
            aria-hidden="true"
          ></span></template>
        </h1>

        <p class="ic-lhero__sub">Cerca casa e guarda subito come potrebbe diventare.</p>

        <form class="ic-lsearch" role="search" aria-label="Cerca casa" @submit.prevent="search">
          <!-- Each field is a labelled box with a transparent native <select>
               stretched over it: the look of a portal search bar, the native
               picker (and keyboard / screen-reader support) underneath. -->
          <label class="ic-lsearch__field">
            <MapPin class="ic-lsearch__icon" :size="22" aria-hidden="true" />
            <span class="ic-lsearch__text">
              <span class="ic-lsearch__label">Dove</span>
              <span class="ic-lsearch__value" :class="{ 'is-placeholder': city === neutralCity }">{{ city }}</span>
            </span>
            <ChevronDown class="ic-lsearch__chev" :size="18" aria-hidden="true" />
            <select v-model="city" data-testid="city-select" class="ic-lsearch__select" aria-label="Città">
              <option :value="neutralCity">{{ neutralCity }}</option>
              <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
            </select>
          </label>

          <label class="ic-lsearch__field">
            <House class="ic-lsearch__icon" :size="22" aria-hidden="true" />
            <span class="ic-lsearch__text">
              <span class="ic-lsearch__label">Tipologia</span>
              <span class="ic-lsearch__value" :class="{ 'is-placeholder': typology === typologyOptions[0] }">{{ typology }}</span>
            </span>
            <ChevronDown class="ic-lsearch__chev" :size="18" aria-hidden="true" />
            <select v-model="typology" data-testid="typology-select" class="ic-lsearch__select" aria-label="Tipologia di immobile">
              <option v-for="t in typologyOptions" :key="t" :value="t">{{ t }}</option>
            </select>
          </label>

          <button type="submit" data-testid="search-btn" class="ic-home-btn ic-lsearch__btn">
            <Search :size="22" aria-hidden="true" />
            Cerca
          </button>
        </form>

        <nav v-if="topCities.length" class="ic-lhero__quick" aria-label="Esplora per città">
          <span class="ic-lhero__quick-label">Esplora</span>
          <button
            v-for="c in topCities"
            :key="c.city"
            type="button"
            class="ic-lhero__chip"
            data-testid="city-chip"
            @click="searchCity(c.city)"
          >
            {{ c.city }}
            <span class="ic-lhero__chip-count">{{ c.count }}</span>
          </button>
        </nav>
      </div>

      <!-- Su telefono la landing porta dritta alla ricerca: niente vetrina
           (e niente foto scaricate per niente). -->
      <div v-if="showShowcase" class="ic-lhero__media">
        <home-showcase :slides="slides" :fallback="showcaseFallback" />
      </div>
    </div>
  </section>
</template>

<script>
import { t, i18nState } from '@/i18n';
import { Search, MapPin, House, ChevronDown } from 'lucide-vue-next';
import HomeShowcase from '@/components/home/HomeShowcase.vue';
import {
  buildSearchQuery,
  TYPOLOGY_OPTIONS,
  NEUTRAL_TYPOLOGY,
  NEUTRAL_CITY,
} from '@/services/homeSearch.js';

// Stesso breakpoint "telefono" di home.css (max-width: 767.98px).
function phoneQuery() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 767.98px)')
    : null;
}

export default {
  name: 'HomeHero',
  components: { Search, MapPin, House, ChevronDown, HomeShowcase },
  props: {
    cities: { type: Array, default: () => [] },
    // [{ city, count }] — the cities with the most listings, for quick chips.
    topCities: { type: Array, default: () => [] },
    // Active listings on the portal (0 while loading → the counter stays hidden).
    totalCount: { type: Number, default: 0 },
    // Recent listings for the photo → render showcase (pickShowcaseSlides()).
    slides: { type: Array, default: () => [] },
    // True when the listings are known to be unavailable (see HomeShowcase).
    showcaseFallback: { type: Boolean, default: false },
  },
  data() {
    return {
      typologyOptions: TYPOLOGY_OPTIONS,
      neutralCity: NEUTRAL_CITY,
      typology: NEUTRAL_TYPOLOGY,
      city: NEUTRAL_CITY,
      showShowcase: !phoneQuery() || !phoneQuery().matches,
    };
  },
  mounted() {
    this.phoneMq = phoneQuery();
    if (this.phoneMq && this.phoneMq.addEventListener) this.phoneMq.addEventListener('change', this.onPhoneChange);
  },
  beforeUnmount() {
    if (this.phoneMq && this.phoneMq.removeEventListener) this.phoneMq.removeEventListener('change', this.onPhoneChange);
  },
  computed: {
    totalCountLabel() {
      const n = this.totalCount;
      return n === 1 ? '1 casa in vendita ora' : `${n.toLocaleString('it-IT')} case in vendita ora`;
    },
    // Il claim parola per parola non passa dalla traduzione della pagina:
    // si traduce intero qui (e si ricalcola quando cambia la lingua).
    titleText() {
      return i18nState.locale && t('Se la puoi immaginare, la puoi realizzare.');
    },
    titleWords() {
      // Per-word entrance delays, with an extra pause after "immaginare,"
      // so the second phrase lands after a beat.
      const stagger = 0.14; // seconds between words
      const phraseGap = 0.5; // extra lag after the comma
      let acc = 0;
      return this.titleText.split(' ').map((text) => {
        // `breakAfter` marca la parola con la virgola: forza l'a capo subito
        // dopo, così il claim si spezza sempre lì quando va su due righe.
        const item = { text, delay: +acc.toFixed(2), breakAfter: text.endsWith(',') };
        acc += stagger;
        if (text.endsWith(',')) acc += phraseGap;
        return item;
      });
    },
  },
  methods: {
    onPhoneChange(e) {
      this.showShowcase = !e.matches;
    },
    search() {
      const query = buildSearchQuery({ typology: this.typology, city: this.city });
      this.$router.push({ path: '/annunci', query });
    },
    searchCity(city) {
      this.$router.push({ path: '/annunci', query: buildSearchQuery({ city }) });
    },
  },
};
</script>
